import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { DTypeFormSelectField } from '@grupo-constant/next-dtype/form';
import { DTypeViewableCol } from '@grupo-constant/next-dtype/types';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { DTypeFormFieldComponent } from '../../dtype-field.model';
import { LocalizePipe } from '../../../pipes/localize.pipe';

@Component({
    selector: 'select-field-component',
    templateUrl: './select-field.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DtypeSelectFieldComponent implements DTypeFormFieldComponent, OnInit, OnDestroy {

    @Input() field: DTypeFormSelectField;
    @Input() isUpdate: boolean;
    @Input() useSummaryView: boolean;

    displayedValue: string;

    private _unsubscribeAll = new Subject<void>();

    constructor(private localize: LocalizePipe) { }

    ngOnInit() {
        const readonlyOnUpdate = (this.field.dTypeCol as DTypeViewableCol).readonlyOnUpdate;
        if (readonlyOnUpdate && this.isUpdate) {
            this.field.readonly = true;
            this.field.control.disable({ emitEvent: false });
        }

        if (this.field.options.length === 1) {
            if (this.field.autoselect) {
                this.field.control.setValue(this.field.options[0].code);
            }
        }

        if (this.field.multiple) {
            let val: string;
            const values: string[] = [];
            if (typeof this.field.control.value === 'string') {
                for (const section of this.field.control.value.split(',') ?? []) {
                    for (const match of section.match(/[a-zA-Z0-9]/g)) {
                        val = val === undefined ? match : val.concat(match);
                    }
                    values.push(val);
                    val = undefined;
                }
                this.field.control.setValue(values);
            }
        }
        this.displayedValue = this.getDisplayValue(this.field.control.value);

        this.field.control.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                distinctUntilChanged()
            )
            .subscribe(value => this.displayedValue = this.getDisplayValue(value));
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    private getDisplayValue(value: string | string[]) {
        if (this.field.distinctValues) {
            if (typeof value === 'string' && value.length > 0) {
                return value;
            } else if (Array.isArray(value) && value.length > 0) {
                return value.join(', ');
            }
            return '';
        }
        if (value === undefined || value === null) {
            return '';
        }
        let displayValue = '';
        if (typeof value === 'string' && value.length > 0) {
            displayValue = this.getOptionLabel(value);
        } else if (Array.isArray(value) && value.length > 0) {
            let optionLabel = '';
            for (const code of value) {
                optionLabel = this.getOptionLabel(code);
                if (optionLabel) {
                    if (displayValue.length > 0) {
                        displayValue += ', ';
                    }
                    displayValue += optionLabel;
                }
            }
        }
        if (!displayValue && value) {
            this.field.control.setValue(undefined);
        }
        return displayValue ?? '';
    }

    private getOptionLabel(code: string) {
        if (!code) {
            return null;
        }
        const option = this.field.options.find(o => o.code === code);
        return option ? this.localize.transform(option.value) : null;
    }

}
